<template>
  <div ref="element" @click="toggleZoom">
    <div :style="styles" class="flex size-full">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { DragGesture, PinchGesture } from '@use-gesture/vanilla'

const element = useTemplateRef('element')

const props = defineProps<{
  active: boolean
}>()

const x = ref(0)
const y = ref(0)
const zoom = ref(1)

const styles = computed(() => {
  const xPos = x.value
  const yPos = y.value
  const scale = zoom.value

  const cssStyles = {
    transform: `translate(${scale === 1 ? 0 : xPos}px, ${scale === 1 ? 0 : yPos}px) scale(${scale})`,
  }

  return props.active ? cssStyles : undefined
})

const reset = () => {
  x.value = 0
  y.value = 0
  zoom.value = 1
}

let endWatch: (() => void) | undefined
let dragGesture: DragGesture | undefined
let pinchGesture: PinchGesture | undefined

onMounted(() => {
  endWatch = watch(
    () => props.active,
    (newValue) => {
      if (newValue) {
        if (!element.value) {
          return
        }

        dragGesture = new DragGesture(
          element.value,
          (e) => {
            x.value = e.offset[0]
            y.value = e.offset[1]
          },
          {
            eventOptions: {
              capture: true,
            },
          }
        )

        pinchGesture = new PinchGesture(
          element.value,
          (e) => {
            zoom.value = e.offset[0]

            if (e.offset[0] === 1) {
              reset()
            }
          },
          {
            scaleBounds: {
              min: 1,
              max: 4,
            },
            eventOptions: {
              capture: true,
            },
          }
        )
      } else {
        reset()
        dragGesture?.destroy()
        pinchGesture?.destroy()
      }
    },
    {
      immediate: true,
    }
  )
})

onBeforeUnmount(() => {
  if (endWatch) {
    endWatch()
  }

  reset()
  dragGesture?.destroy()
  pinchGesture?.destroy()
})

const toggleZoom = (evt: UIEvent) => {
  if (evt.detail === 2) {
    if (zoom.value === 1) {
      zoom.value = 2
    } else {
      zoom.value = 1
    }
  }
}

const emit = defineEmits<{
  'zoom-change': [boolean]
}>()

watch(zoom, (newValue) => {
  emit('zoom-change', newValue !== 1)
})
</script>
